import { OrgApiError } from "@/services/orgApiFetch";

export type ClientFormErrorContext =
    | "general"
    | "onboarding"
    | "policy"
    | "position"
    | "program"
    | "savedDestination"
    | "settings"
    | "team";

interface ErrorDetails {
    message: string;
    normalizedMessage: string;
    code?: string;
    normalizedCode?: string;
    status?: number;
}

type ErrorCopy = string | ((context: ClientFormErrorContext) => string);

interface ErrorMatcher {
    pattern: RegExp | ((details: ErrorDetails) => boolean);
    copy: ErrorCopy;
}

const DEFAULT_BY_CONTEXT: Record<ClientFormErrorContext, string> = {
    general: "The request could not be completed. Check your inputs and try again.",
    onboarding: "Onboarding was not saved. Check the step details, then try again.",
    policy: "Policy was not saved. Check the value and distributor constraints, then try again.",
    position: "Position was not submitted. Check the wallet and Program Surface, then try again.",
    program: "Program was not created. Check the parent Program and funding settings, then try again.",
    savedDestination: "Destination was not saved. Check the address and try again.",
    settings: "Profile change was not saved. Check your input and try again.",
    team: "Team update was not saved. Check the member details and try again."
};

const COMMON_MATCHERS: ErrorMatcher[] = [
    {
        pattern: /too many requests|rate limit/,
        copy: "Too many attempts. Wait a few minutes, then try again."
    },
    {
        pattern: /organization scope required/,
        copy: "Your session is missing its organization context. Sign out and sign back in, then retry."
    },
    {
        pattern: /organization not found/,
        copy: "Your organization could not be found. Sign out and sign back in, then retry."
    },
    {
        pattern: /client not found/,
        copy: "Your company recipient record could not be found. Sign out and sign back in, then retry."
    },
    {
        pattern: /onboarding is not approved yet/,
        copy: "This action is blocked until onboarding is approved. Wait for distributor approval, then retry."
    }
];

const CONTEXT_MATCHERS: Record<ClientFormErrorContext, ErrorMatcher[]> = {
    general: [],
    onboarding: [
        {
            pattern: /application not found/,
            copy: "Onboarding could not load. Refresh the page. If it still fails, ask your distributor to re-open onboarding."
        },
        {
            pattern: /step not found/,
            copy: "This onboarding step could not be found. Refresh onboarding and try the step again."
        },
        {
            pattern: /step is not applicant-driven/,
            copy: "This step cannot be edited from the Company Recipient Console. Complete the previous distributor-controlled step first."
        },
        {
            pattern: /terminal state|already in a terminal state/,
            copy: "Onboarding cannot be changed because the application is already complete or closed."
        },
        {
            pattern: /step is already complete/,
            copy: "This onboarding step is already complete. Refresh the page to see the latest status."
        },
        {
            pattern:
                /multipart body required|missing file part|missing 'purpose' field|unsupported 'purpose' query value/,
            copy: "Document upload failed. Choose the file again and retry from this onboarding step."
        },
        {
            pattern: /document not found|document content unavailable/,
            copy: "That document could not be found. Refresh the document list and upload it again if needed."
        },
        {
            pattern: /file too large|payload too large/,
            copy: "Document upload failed. Choose a smaller file and retry."
        },
        {
            pattern: /lite onboarding disabled|lite_onboarding_disabled/,
            copy: "Lite onboarding is not available for this company recipient. Use the full onboarding flow instead."
        },
        {
            // The issuer's side of the MSA (legal identity, DocuSign
            // counter-signer) is incomplete. Nothing the applicant enters
            // can fix that, so the copy must not send them back to their
            // own details. Matched on the stable code first, message second.
            pattern: /issuer_agreement_signing_not_configured|issuer agreement signing is not configured/,
            copy: "Your distributor has not finished setting up agreement signing, so the MSA cannot be sent yet. Nothing is needed from you. Ask your distributor to complete their agreement signing settings, then retry."
        },
        {
            pattern: /minting_operator_not_configured|minting operator is not configured/,
            copy: "The Minting Master Agreement cannot be sent yet because the operator's signing details are not set up. Ask your distributor to complete them, then retry."
        },
        {
            // A resubmit while the envelopes are in the signatory's inbox.
            // Only the issuer can withdraw them, so the copy sends the
            // applicant there rather than back to the form.
            pattern: /agreements are out for signature/,
            copy: "The agreements are out for signature, so the application cannot be changed right now. Ask your distributor to withdraw them if something needs correcting."
        },
        {
            // The platform is not sending agreements at all right now
            // (yaas-api ONBOARDING_AGREEMENTS_VIA_DOCUSIGN is off). Nothing
            // to retry and nothing to fix on the client's side.
            pattern: /agreements_handled_outside_platform|handled outside the platform/,
            copy: "Agreements are being handled outside the platform for now, so nothing can be sent from here. Your distributor will be in touch about signing."
        },
        {
            pattern: /msa|docusign|envelope/,
            copy: "The MSA signing request could not be created. Complete earlier onboarding steps and verify signer details, then retry."
        },
        {
            pattern: /missing|required|complete.*section|not complete/,
            copy: "Onboarding was not submitted. Complete the highlighted sections, then submit again."
        }
    ],
    policy: [
        {
            pattern: /at least one program target/,
            copy: "Policy was not created. Select at least one active Program before saving."
        },
        {
            pattern: /policy not found/,
            copy: "Policy was not saved. Refresh policies and select the policy again."
        },
        {
            pattern: /unsupported ruletype/,
            copy: "Policy was not saved. This rule type is not available for company recipient configuration."
        },
        {
            pattern: /parameters are invalid|parameters do not satisfy|out of bounds/,
            copy: "Policy was not saved. The value is outside the distributor's allowed boundary. Adjust it within the shown constraints."
        },
        {
            pattern: /no parent permission boundary|permission boundary is locked|parent constraints are missing/,
            copy: "Policy was not saved. The distributor permission boundary is locked or missing, so this company recipient cannot configure it."
        },
        {
            pattern: /already exists for this program and ruletype|policy instance already exists/,
            copy: "Policy was not created. An active policy of this type already exists for that Program."
        },
        {
            pattern: /cannot set permission or constraintconfig/,
            copy: "Policy was not saved. Company recipient policies can only set the value, not distributor permissions or boundaries."
        },
        {
            pattern: /client-run policies require/,
            copy: "Policy was not saved. This Program is not eligible for company recipient-run policy configuration."
        }
    ],
    position: [
        {
            pattern:
                /active program position already exists|program position.*already exists|position already exists|already registered with this issuer/,
            copy: "This wallet is already registered. Manage the existing position instead."
        },
        {
            pattern: /wallet must be verified|wallet verification is required|verification signature is required/,
            copy: "Position was not submitted. Go back to Verify and complete wallet verification first."
        },
        {
            pattern: /verification proof has already been used/,
            copy: "This signature has already been used. Sign a fresh message."
        },
        {
            pattern: /wallet verification failed/,
            copy: "We couldn't accept the wallet verification. It may have expired. Go back to the Verify step and verify again."
        },
        {
            pattern: /invalid wallet address|invalid solana address/,
            copy: "Enter a valid Solana wallet address."
        },
        {
            pattern: /invalid rewards destination address/,
            copy: "Enter a valid Solana rewards destination address."
        },
        {
            pattern:
                /unknown verification mode|direct solomon verification cannot include|invalid position source kind/,
            copy: "Position was not submitted. Refresh and restart verification, then try again."
        },
        {
            pattern: /eoa positions cannot include multisig metadata/,
            copy: "Position was not submitted. Restart verification and choose the correct wallet type."
        },
        {
            pattern: /invalid multisig signer/,
            copy: "Signer address is invalid."
        },
        {
            pattern: /signer must differ/,
            copy: "The signer must be different from the multisig vault. Connect a signer wallet that belongs to the multisig."
        },
        {
            pattern: /unsupported multisig program/,
            copy: "Only Squads V4 multisigs are supported right now."
        },
        {
            pattern: /program surface not found/,
            copy: "Position was not submitted. Refresh Program Surfaces and choose an active Program Surface."
        },
        {
            pattern: /active program surface|active program/,
            copy: "Position was not submitted. This Program Surface is no longer active. Choose another active surface."
        },
        {
            pattern: /cannot edit destination of .* position/,
            copy: "Rewards destination was not updated. This position's current status does not allow destination changes."
        },
        {
            pattern: /cannot resubmit .* position/,
            copy: "Position was not resubmitted. Only rejected positions can be sent back for review."
        }
    ],
    program: [
        {
            pattern: /program name is required/,
            copy: "Program was not created. Add a Program name before launching."
        },
        {
            pattern: /must reference exactly one issuer-run parent program/,
            copy: "Program was not created. Choose exactly one distributor-run parent Program."
        },
        {
            pattern: /require a client end-user enrollments issuer program/,
            copy: "Program was not created. The parent Program is not a Company Recipient End-User Enrollments Program."
        },
        {
            pattern: /can only be created from an active issuer-run program|only active programs permit|ended programs/,
            copy: "Program was not created. Company Recipient-run Programs can only launch from an active distributor Program."
        },
        {
            pattern: /does not have an active program assignment/,
            copy: "Program was not created. This company recipient does not have an active assignment for the selected parent Program."
        },
        {
            pattern: /program not found/,
            copy: "Program was not created. Refresh Programs and choose an available parent Program."
        }
    ],
    savedDestination: [
        {
            pattern: /this address is already saved/,
            copy: "Destination was not saved. This address is already in your saved destinations. Use the existing destination or enter a different address."
        },
        {
            pattern: /name cannot be empty/,
            copy: "Destination was not saved. Add a label or use the address as the label."
        },
        {
            pattern: /saved destination not found/,
            copy: "Destination was not updated. Refresh saved destinations and try again."
        },
        {
            pattern: /position splits|used by one or more/,
            copy: "Destination was not deleted. One or more position splits still use it. Move those rewards before deleting this destination."
        },
        {
            pattern: /invalid.*address|valid solana/,
            copy: "Enter a valid Solana rewards destination address."
        }
    ],
    settings: [
        {
            pattern: /current password is incorrect/,
            copy: "The current password does not match. Re-enter it and try again."
        },
        {
            pattern: /password must be between 8 and 72 characters/,
            copy: "Password must be between 8 and 72 characters."
        },
        {
            pattern: /only admin role can change another user's fullname/,
            copy: "Only company recipient Admins can change another user's name. Ask an Admin to make this update."
        }
    ],
    team: [
        {
            pattern: /only admin role can|admin role required/,
            copy: "Only Owners can manage teammates. Ask an Owner to make this change or update your role."
        },
        {
            pattern: /org user with (this|that) email already exists/,
            copy: "That email is already on the team. Invite a different address."
        },
        {
            pattern: /reset token is already outstanding/,
            copy: "An invitation link is already active. Use that link or try again later."
        },
        {
            pattern: /user already claimed.*email is immutable/,
            copy: "Account already claimed, so its email is fixed. Invite a different address instead."
        },
        {
            pattern: /cannot demote the last admin|cannot suspend the last admin/,
            copy: "An organization needs at least one Owner. Make another member an Owner first."
        },
        {
            pattern: /user not in your organization|not authorized|user not found/,
            copy: "Member not found in this organization. Refresh the page and try again."
        },
        {
            pattern: /cannot regenerate claim url for user with status|cannot cancel invitation for user with status/,
            copy: "This invitation can no longer change. Refresh the page to see its status."
        }
    ]
};

// Browsers and fetch wrappers may append a host or URL to transport errors.
// Handle these before domain matchers, which could mistake URL text for validation.
const CONNECTION_ERROR_PATTERN =
    /^(?:typeerror:\s*)?(?:failed to fetch|load failed|networkerror|network request failed)\b/i;

const TECHNICAL_MESSAGE_PATTERN =
    /^(request failed|upload failed): \d+$|^failed to fetch$|^load failed$|^networkerror\b|^unexpected token\b|^internal server error$|^bad request$|^unauthorized$|^forbidden$|^not found$/i;

/** What a form says when the request never reached the API (it's down or
 *  restarting). Forms pair it with a Retry action. */
export const CONNECTION_ERROR_MESSAGE = "Cannot reach the server, please try again in a moment.";

/** The request never got an HTTP response: the server is unreachable, not
 *  refusing. Such a failure is worth retrying as-is. */
export function isClientConnectionError(error: unknown): boolean {
    const details = readErrorDetails(error);
    return details.status === undefined && CONNECTION_ERROR_PATTERN.test(details.normalizedMessage);
}

export function resolveClientFormError(
    error: unknown,
    context: ClientFormErrorContext = "general",
    fallback = DEFAULT_BY_CONTEXT[context]
): string {
    const details = readErrorDetails(error);
    if (isClientConnectionError(error)) {
        return CONNECTION_ERROR_MESSAGE;
    }
    const match =
        findMatch(CONTEXT_MATCHERS[context], details, context) ?? findMatch(COMMON_MATCHERS, details, context);
    if (match) return match;

    const statusCopy = statusFallback(details.status, context);
    if (statusCopy) return statusCopy;

    if (shouldExposeRawMessage(details.message, details.status)) {
        return details.message;
    }

    return fallback || DEFAULT_BY_CONTEXT[context];
}

export function asClientFormError(
    error: unknown,
    context: ClientFormErrorContext = "general",
    fallback?: string
): Error {
    return new Error(resolveClientFormError(error, context, fallback));
}

export function readClientFormErrorCode(error: unknown): string | undefined {
    if (error instanceof OrgApiError) return error.code;
    if (typeof error !== "object" || error === null) return undefined;
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
}

export function readClientFormErrorStatus(error: unknown): number | undefined {
    if (error instanceof OrgApiError) return error.status;
    if (typeof error === "object" && error !== null) {
        const status = (error as { status?: unknown }).status;
        if (typeof status === "number") return status;
    }
    const message = readErrorMessage(error);
    const match = /\b(?:request|upload) failed:\s*(\d{3})\b/i.exec(message);
    if (!match) return undefined;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function readErrorDetails(error: unknown): ErrorDetails {
    const message = readErrorMessage(error);
    const code = readClientFormErrorCode(error);
    return {
        message,
        normalizedMessage: normalize(message),
        ...(code
            ? {
                  code,
                  normalizedCode: normalize(code)
              }
            : {}),
        ...(readClientFormErrorStatus(error) !== undefined ? { status: readClientFormErrorStatus(error) } : {})
    };
}

function readErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message.trim();
    if (typeof error === "string") return error.trim();
    if (typeof error !== "object" || error === null) return "";
    const message = (error as { message?: unknown; error?: unknown }).message;
    if (typeof message === "string") return message.trim();
    const alternate = (error as { error?: unknown }).error;
    return typeof alternate === "string" ? alternate.trim() : "";
}

function findMatch(
    matchers: ErrorMatcher[],
    details: ErrorDetails,
    context: ClientFormErrorContext
): string | undefined {
    for (const matcher of matchers) {
        const matched =
            typeof matcher.pattern === "function"
                ? matcher.pattern(details)
                : matcher.pattern.test(details.normalizedMessage) ||
                  (details.normalizedCode ? matcher.pattern.test(details.normalizedCode) : false);
        if (!matched) continue;
        return typeof matcher.copy === "function" ? matcher.copy(context) : matcher.copy;
    }
    return undefined;
}

function statusFallback(status: number | undefined, context: ClientFormErrorContext): string | undefined {
    if (status === undefined) return undefined;
    if (status === 401) {
        return "Your session could not be verified. Sign in again, then retry.";
    }
    if (status === 403) return permissionCopy(context);
    if (status === 404) return notFoundCopy(context);
    if (status === 409) return conflictCopy(context);
    if (status === 413) {
        return context === "onboarding"
            ? "Document upload failed. Choose a smaller file and retry."
            : "The file or request is too large. Reduce it and try again.";
    }
    if (status === 429) {
        return "Too many attempts. Wait a few minutes, then try again.";
    }
    if (status >= 500) return serverCopy(context);
    return undefined;
}

function permissionCopy(context: ClientFormErrorContext): string {
    if (context === "team") {
        return "Only Owners can manage teammates. Ask an Owner to make this change or update your role.";
    }
    if (context === "onboarding") {
        return "Only company recipient Admins can submit onboarding changes. Ask a company recipient Admin to finish this step.";
    }
    return "You do not have permission to complete this action. Ask a company recipient Admin for access.";
}

function notFoundCopy(context: ClientFormErrorContext): string {
    switch (context) {
        case "onboarding":
            return "Onboarding could not be found. Refresh the page and try again.";
        case "policy":
            return "Policy was not found. Refresh policies and select it again.";
        case "position":
            return "Position was not found. Refresh positions and try again.";
        case "program":
            return "Program was not found. Refresh Programs and try again.";
        case "savedDestination":
            return "Destination was not found. Refresh saved destinations and try again.";
        case "team":
            return "This teammate could not be found. Refresh the Team page and try again.";
        case "general":
        case "settings":
        default:
            return "The record could not be found. Refresh and try again.";
    }
}

function conflictCopy(context: ClientFormErrorContext): string {
    switch (context) {
        case "policy":
            return "Policy was not saved because an active policy already exists for this Program.";
        case "position":
            return "This wallet is already registered. Manage the existing position instead.";
        case "savedDestination":
            return "Destination was not saved because that address is already saved.";
        case "team":
            return "Team update was not saved because the email or invite already exists.";
        case "general":
        case "onboarding":
        case "program":
        case "settings":
        default:
            return "The request conflicts with an existing record. Refresh and try again.";
    }
}

function serverCopy(context: ClientFormErrorContext): string {
    switch (context) {
        case "onboarding":
            return "Onboarding was not saved because the server hit an issue. Try again in a moment.";
        case "policy":
            return "Policy was not saved because the server hit an issue. Try again in a moment.";
        case "position":
            return "Position was not submitted because the server hit an issue. Try again in a moment.";
        case "program":
            return "Program was not created because the server hit an issue. Try again in a moment.";
        case "savedDestination":
            return "Destination was not saved because the server hit an issue. Try again in a moment.";
        case "settings":
            return "Profile change was not saved because the server hit an issue. Try again in a moment.";
        case "team":
            return "Team update was not saved because the server hit an issue. Try again in a moment.";
        case "general":
        default:
            return "The server hit an issue. Try again in a moment.";
    }
}

function shouldExposeRawMessage(message: string, status: number | undefined): boolean {
    if (!message) return false;
    if (status !== undefined && status >= 500) return false;
    return !TECHNICAL_MESSAGE_PATTERN.test(message);
}

function normalize(value: string): string {
    return value
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
}
